import { useContext, useEffect, useState } from 'react';
import { VerticalDivider } from 'src/v2/commonComponents/VerticalDivider';
import {
  useBreakpoint,
  useCompareMode,
  useComparingConversationsParams,
  useRightSidePanelState,
  useQueryParams,
} from 'src/hooks';
import { CompareModeColumn } from './components/CompareModeColumn';
import styles from './CompareModeView.module.scss';
import {
  FIRST_COMPARE_MODEL_ANCHOR,
  SECOND_COMPARE_MODEL_ANCHOR,
} from 'src/constants';
import ForwardRefContext from 'src/contexts/ForwardRefContext';
import ThreadInputBoxContext from 'src/contexts/ThreadInputBoxContext';
import classNames from 'classnames';

type CurrentCompareModelType =
  | typeof FIRST_COMPARE_MODEL_ANCHOR
  | typeof SECOND_COMPARE_MODEL_ANCHOR;

export const CompareModeView = () => {
  const { removeSearchParam, setSearchParams } = useQueryParams();

  const [currentDisplayedColumn, setCurrentDisplayedColumn] =
    useState<CurrentCompareModelType | null>(null);

  const { isLaptopLAndUp } = useBreakpoint();
  const { isRightPanelExpanded } = useRightSidePanelState();
  const { firstComparedConvId, secondComparedConvId } =
    useComparingConversationsParams();

  const {
    changeFirstCompareModel,
    changeSecondCompareModel,
    firstCompareModel,
    secondCompareModel,
  } = useCompareMode();

  const { mainMessageListRef, compareMessageListRef } =
    useContext(ForwardRefContext);

  const { isInputBoxExpanded } = useContext(ThreadInputBoxContext);

  const shouldDisplayAsOneColumn = isRightPanelExpanded || !isLaptopLAndUp;

  useEffect(() => {
    if (shouldDisplayAsOneColumn && currentDisplayedColumn === null) {
      setCurrentDisplayedColumn(FIRST_COMPARE_MODEL_ANCHOR);
      setSearchParams({ active_compare_list: 'first' });
      return;
    }
    if (!isRightPanelExpanded || !isLaptopLAndUp) {
      removeSearchParam('active_compare_list');
      setCurrentDisplayedColumn(null);
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRightPanelExpanded, isLaptopLAndUp]);

  const toggleCurrentColumn = () => {
    if (currentDisplayedColumn === FIRST_COMPARE_MODEL_ANCHOR) {
      setCurrentDisplayedColumn(SECOND_COMPARE_MODEL_ANCHOR);
      setSearchParams({ active_compare_list: 'second' });
    } else {
      setCurrentDisplayedColumn(FIRST_COMPARE_MODEL_ANCHOR);
      setSearchParams({ active_compare_list: 'first' });
    }
  };

  const renderFirstColumn = () => (
    <CompareModeColumn
      conversationId={firstComparedConvId}
      onComparingModelChange={changeFirstCompareModel}
      compareModel={firstCompareModel}
      toggleCurrentColumn={toggleCurrentColumn}
      currentDisplayedColumn={currentDisplayedColumn}
      messageListRef={mainMessageListRef}
      compareType="first"
    />
  );

  const renderSecondColumn = () => (
    <CompareModeColumn
      conversationId={secondComparedConvId}
      onComparingModelChange={changeSecondCompareModel}
      compareModel={secondCompareModel}
      toggleCurrentColumn={toggleCurrentColumn}
      currentDisplayedColumn={currentDisplayedColumn}
      messageListRef={compareMessageListRef}
      compareType="second"
    />
  );

  return (
    <div
      className={classNames(styles.root, {
        [styles.reducedHeight]: isInputBoxExpanded,
      })}
    >
      <div className={styles.container}>
        {shouldDisplayAsOneColumn ? (
          currentDisplayedColumn === FIRST_COMPARE_MODEL_ANCHOR ? (
            renderFirstColumn()
          ) : (
            renderSecondColumn()
          )
        ) : (
          <>
            {renderFirstColumn()}
            <VerticalDivider />
            {renderSecondColumn()}
          </>
        )}
      </div>
    </div>
  );
};
